import { nextTick, ref } from 'vue'

import { useModules } from '@courses/categories-layer/composables/useModules'
import { useLibrary } from '@courses/library-layer/composables/useLibrary'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

import { useLocation } from '@common/location-layer/composables/useLocation'

import { buildCourseModuleLessonRedirectTarget, parseCourseRedirectUrl } from '../utils/course-redirect.utils'

export const useCourseRedirect = () => {
  const { locationId } = useLocation()
  const { CPNavigateTo } = useCPNavigation()
  const productId = ref('')
  const productLocationId = ref('')
  const moduleId = ref('')

  const { fetchAllProducts } = useLibrary(locationId)
  const allProductsQuery = fetchAllProducts()

  const { fetchModuleLessons } = useModules(productId, productLocationId)
  const moduleLessonsQuery = fetchModuleLessons(moduleId)

  const resolveCourseRedirectTarget = async (redirectionUrl: string) => {
    const parsedRedirectUrl = parseCourseRedirectUrl(redirectionUrl)

    if (parsedRedirectUrl.type === 'direct') {
      return parsedRedirectUrl
    }

    try {
      const productsResult = allProductsQuery.data.value?.length ? { data: allProductsQuery.data.value } : await allProductsQuery.refetch()
      const products = productsResult.data || []

      productId.value = parsedRedirectUrl.productId
      productLocationId.value = products.find(product => product.id === parsedRedirectUrl.productId)?.locationId || locationId.value
      moduleId.value = parsedRedirectUrl.moduleId

      await nextTick()

      const moduleLessonsResult = await moduleLessonsQuery.refetch()
      const firstLessonId = moduleLessonsResult.data?.lessons?.[0]?.id

      if (!firstLessonId) {
        return {
          type: 'direct' as const,
          target: parsedRedirectUrl.fallbackTarget,
          getDefaultTemplate: parsedRedirectUrl.getDefaultTemplate,
        }
      }

      return {
        type: 'direct' as const,
        target: buildCourseModuleLessonRedirectTarget(
          parsedRedirectUrl.productId,
          parsedRedirectUrl.moduleId,
          firstLessonId,
          parsedRedirectUrl.query
        ),
        getDefaultTemplate: parsedRedirectUrl.getDefaultTemplate,
      }
    } catch {
      return {
        type: 'direct' as const,
        target: parsedRedirectUrl.fallbackTarget,
        getDefaultTemplate: parsedRedirectUrl.getDefaultTemplate,
      }
    }
  }

  const redirectToCourse = async (redirectionUrl: string, extraQuery?: Record<string, string>) => {
    const resolvedRedirect = await resolveCourseRedirectTarget(redirectionUrl)

    await CPNavigateTo(
      {
        ...resolvedRedirect.target,
        query: {
          ...(resolvedRedirect.target.query || {}),
          ...(extraQuery || {}),
        },
      },
      resolvedRedirect.getDefaultTemplate
    )
  }

  return {
    resolveCourseRedirectTarget,
    redirectToCourse,
  }
}
